'use client';

import React, { useState, useEffect } from 'react';
import { SlideOverDrawer } from '@/components/ui/SlideOverDrawer';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { 
  Phone, 
  MessageSquare, 
  Calendar, 
  Star, 
  FileText, 
  Edit3, 
  Save, 
  User, 
  Clock, 
  DollarSign, 
  CheckCircle2, 
  XCircle, 
  AlertCircle,
  ExternalLink,
  Plus
} from 'lucide-react';
import { ClientRecord } from './ClientsTable';

interface ClientDetailDrawerProps {
  client: ClientRecord | null;
  isOpen: boolean;
  onClose: () => void;
  onEditClient: (client: ClientRecord) => void;
  onUpdateClient: (clientId: string, data: any) => Promise<void>;
  onScheduleAppointment?: (client: ClientRecord) => void;
}

export const ClientDetailDrawer: React.FC<ClientDetailDrawerProps> = ({
  client,
  isOpen,
  onClose,
  onEditClient,
  onUpdateClient,
  onScheduleAppointment,
}) => {
  const [activeTab, setActiveTab] = useState<'history' | 'notes' | 'info'>('history');
  const [noteText, setNoteText] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [noteSavedSuccess, setNoteSavedSuccess] = useState(false);
  const [historyAppointments, setHistoryAppointments] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    if (client) {
      setNoteText(client.notes || '');
      setNoteSavedSuccess(false);

      // Cargar historial de turnos
      const fetchHistory = async () => {
        setLoadingHistory(true);
        try {
          const res = await fetch(`/api/bookings?phone=${client.phone}`);
          const data = await res.json();
          if (data.success) {
            setHistoryAppointments(data.appointments || []);
          }
        } catch (err) {
          console.error('Error fetching client appointment history:', err);
        } finally {
          setLoadingHistory(false);
        }
      };

      fetchHistory();
    }
  }, [client]);

  if (!client) return null;

  const handleSaveNotes = async () => {
    setIsSavingNote(true);
    try {
      await onUpdateClient(client.id, { notes: noteText });
      setNoteSavedSuccess(true);
      setTimeout(() => setNoteSavedSuccess(false), 3000);
    } catch (err) {
      console.error('Error saving notes:', err);
    } finally {
      setIsSavingNote(false);
    }
  };

  const openWhatsApp = () => {
    const cleanPhone = client.phone.replace(/[^0-9]/g, '');
    window.open(`https://wa.me/549${cleanPhone}`, '_blank');
  };

  const initials = client.name.trim().slice(0, 2).toUpperCase();

  const getStatusBadge = (status: string) => {
    const s = (status || '').toLowerCase();
    switch (s) {
      case 'confirmed':
        return <Badge variant="confirmed" withDot>Confirmado</Badge>;
      case 'completed':
        return <Badge variant="completed" withDot>Cobrado</Badge>;
      case 'attended':
        return <Badge variant="attended" withDot>En sala</Badge>;
      case 'no_show':
        return <Badge variant="noshow" withDot>No asistió</Badge>;
      case 'cancelled':
        return <Badge variant="cancelled" withDot>Cancelado</Badge>;
      default:
        return <Badge variant="default">{status}</Badge>;
    }
  };

  return (
    <SlideOverDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={`Ficha de ${client.name}`}
      subtitle="Perfil 360°, métricas históricas y notas privadas"
    >
      <div className="flex flex-col h-full">
        {/* Cabecera del Cliente */}
        <div className="p-5 border-b border-slate-100 bg-slate-50/50 space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3.5">
              <div className="w-13 h-13 rounded-2xl bg-[#6255F5]/10 text-[#6255F5] flex items-center justify-center font-black text-lg border border-[#6255F5]/20 shadow-inner shrink-0">
                {initials}
              </div>
              <div className="min-w-0">
                <h2 className="text-base font-black text-slate-900 tracking-tight truncate">
                  {client.name}
                </h2>
                <p className="text-xs text-slate-500 font-mono mt-0.5">
                  +54 {client.phone}
                </p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {client.tags && client.tags.length > 0 ? (
                    client.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 text-[10px] font-bold rounded-lg bg-white border border-slate-200 text-slate-700 shadow-xs"
                      >
                        {tag}
                      </span>
                    ))
                  ) : (
                    <span className="px-2 py-0.5 text-[10px] font-bold rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200">
                      Cliente Activo
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEditClient(client)}
                title="Editar datos personales"
              >
                <Edit3 className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {/* Botón Directo WhatsApp */}
          <div className="flex items-center gap-2">
            <button
              onClick={openWhatsApp}
              className="flex-1 py-2 px-3 rounded-xl bg-[#25D366] hover:bg-[#20ba59] text-white font-bold text-xs flex items-center justify-center gap-2 shadow-sm transition active:scale-98 cursor-pointer"
            >
              <MessageSquare className="w-4 h-4 fill-white" />
              <span>Abrir chat de WhatsApp</span>
            </button>
            {onScheduleAppointment && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => onScheduleAppointment(client)}
                className="shrink-0"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>Agendar</span>
              </Button>
            )}
          </div>

          {/* Tira de Métricas Clave */}
          <div className="grid grid-cols-3 gap-2 pt-2">
            <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 text-center">
              <span className="text-[10px] uppercase font-bold text-slate-400 block">
                Citas
              </span>
              <span className="text-sm font-black text-slate-900 mt-0.5 block">
                {client.appointmentsCount || 0}
              </span>
            </div>
            <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 text-center">
              <span className="text-[10px] uppercase font-bold text-slate-400 block">
                LTV Gastado
              </span>
              <span className="text-sm font-black text-slate-900 mt-0.5 block">
                ${(client.totalSpent || 0).toLocaleString('es-AR')}
              </span>
            </div>
            <div className="bg-white p-2.5 rounded-xl border border-slate-200/80 text-center">
              <span className="text-[10px] uppercase font-bold text-slate-400 block">
                Puntos
              </span>
              <span className="text-sm font-black text-[#6255F5] mt-0.5 block">
                {client.points || 0} pts
              </span>
            </div>
          </div>
        </div>

        {/* Barra de Tabs */}
        <div className="flex items-center border-b border-slate-200 bg-white px-5">
          <button
            onClick={() => setActiveTab('history')}
            className={`py-3 px-3 text-xs font-bold border-b-2 transition cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'history'
                ? 'border-[#6255F5] text-[#6255F5]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            <span>Historial ({historyAppointments.length})</span>
          </button>
          <button
            onClick={() => setActiveTab('notes')}
            className={`py-3 px-3 text-xs font-bold border-b-2 transition cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'notes'
                ? 'border-[#6255F5] text-[#6255F5]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <FileText className="w-3.5 h-3.5" />
            <span>Ficha Técnica</span>
          </button>
          <button
            onClick={() => setActiveTab('info')}
            className={`py-3 px-3 text-xs font-bold border-b-2 transition cursor-pointer flex items-center gap-1.5 ${
              activeTab === 'info'
                ? 'border-[#6255F5] text-[#6255F5]'
                : 'border-transparent text-slate-500 hover:text-slate-800'
            }`}
          >
            <User className="w-3.5 h-3.5" />
            <span>Datos</span>
          </button>
        </div>

        {/* Contenido scrolleable de la tab activa */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* TAB 1: HISTORIAL DE CITAS */}
          {activeTab === 'history' && (
            <div className="space-y-3">
              {loadingHistory ? (
                <div className="py-12 text-center">
                  <div className="w-6 h-6 border-2 border-slate-300 border-t-[#6255F5] rounded-full animate-spin mx-auto mb-2" />
                  <p className="text-xs text-slate-400">Cargando turnos...</p>
                </div>
              ) : historyAppointments.length === 0 ? (
                <div className="py-12 text-center border-2 border-dashed border-slate-200 rounded-2xl p-6">
                  <Calendar className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs font-bold text-slate-700">Sin citas previas registradas</p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Cuando reserve online o por mostrador se listarán aquí.
                  </p>
                </div>
              ) : (
                historyAppointments.map((apt) => (
                  <div
                    key={apt.id}
                    className="p-3.5 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition space-y-2 shadow-2xs"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-xs font-bold text-slate-900">
                          {apt.serviceName}
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {apt.dateStr} · {apt.startTime} hs ({apt.duration} min)
                        </p>
                      </div>
                      <div className="shrink-0">
                        {getStatusBadge(apt.status)}
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-slate-100 text-[11px]">
                      <span className="text-slate-500 font-medium">
                        Atendido por: <strong className="text-slate-700">{apt.staffName || 'Equipo'}</strong>
                      </span>
                      <span className="font-black text-slate-900">
                        ${(apt.price || 0).toLocaleString('es-AR')}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* TAB 2: FICHA TÉCNICA / NOTAS */}
          {activeTab === 'notes' && (
            <div className="space-y-4">
              <div className="p-3 bg-amber-50 border border-amber-200/80 rounded-xl text-amber-900 text-xs flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 text-amber-600 mt-0.5" />
                <p>
                  Estas notas son <strong>estrictamente confidenciales</strong> para el equipo del salón (ej. fórmulas de tintura, sensibilidades o trato personalizado).
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Anotaciones del Profesional
                </label>
                <textarea
                  rows={8}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Escribí aquí las notas técnicas del cliente..."
                  className="w-full p-3.5 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#6255F5]/20 focus:border-[#6255F5] transition"
                />
              </div>

              <div className="flex items-center justify-between">
                {noteSavedSuccess ? (
                  <span className="text-xs font-bold text-emerald-600 flex items-center gap-1">
                    <CheckCircle2 className="w-4 h-4" />
                    ¡Notas guardadas!
                  </span>
                ) : (
                  <span />
                )}
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleSaveNotes}
                  isLoading={isSavingNote}
                >
                  <Save className="w-3.5 h-3.5" />
                  <span>Guardar Notas</span>
                </Button>
              </div>
            </div>
          )}

          {/* TAB 3: DATOS DE CONTACTO */}
          {activeTab === 'info' && (
            <div className="space-y-3">
              <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Teléfono Principal
                </span>
                <p className="text-xs font-bold text-slate-800 font-mono">
                  +54 {client.phone}
                </p>
              </div>

              <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Correo Electrónico
                </span>
                <p className="text-xs font-bold text-slate-800">
                  {client.email || 'No registrado'}
                </p>
              </div>

              <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Documento / DNI
                </span>
                <p className="text-xs font-bold text-slate-800 font-mono">
                  {client.dni || 'No registrado'}
                </p>
              </div>

              <div className="p-3.5 bg-slate-50 border border-slate-200/80 rounded-xl space-y-1">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  Fecha de Alta en Calenddy
                </span>
                <p className="text-xs font-bold text-slate-800">
                  {new Date(client.createdAt).toLocaleDateString('es-AR', {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-100 bg-white flex items-center justify-end">
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </SlideOverDrawer>
  );
};
